import type { ParseKeys } from 'i18next'
import type { TimeUnits } from 'shared/types/time-units'

// --- API / pagination ---

export const PAGINATION_ORDER = {
  DESC: 'desc',
  ASC: 'asc',
} as const

// --- Cookies ---

/**
 * Cookie name for storing the Next.js locale preference.
 * Used for client-side locale persistence across sessions.
 */
export const NEXT_LOCALE_COOKIE = 'NEXT_LOCALE' as const

/**
 * Cookie name for storing the user's dashboard locale preference.
 * Used to sync locale preference with the backend user settings.
 */
const DASHBOARD_LOCALE_COOKIE = 'dashboard_locale' as const

export const LOCALE_COOKIE_NAMES = [
  NEXT_LOCALE_COOKIE,
  DASHBOARD_LOCALE_COOKIE,
  'nextLocale', // Legacy cookie name
  'locale', // Fallback cookie name
] as const

// --- Routes / events / favicon ---

export const DISABLED_PAGE_URL = '/dashboard/disabled'

export const COMMUNITY_ACCESS_DENIED_EVENT = 'community:access-denied'

export const FAVICON_LINK_KEYS = {
  icon: 'icon',
} as const

// --- File source ---

export const FILE_SOURCE = {
  community: 'community',
  settings: 'settings',
} as const

export type FileSource = (typeof FILE_SOURCE)[keyof typeof FILE_SOURCE]

export const DEFAULT_FILE_SOURCE: FileSource = FILE_SOURCE.community

export const COMMUNITY_FILE_SOURCE = 'community'
export const STUDENT_ASSIGNMENT_FILE_SOURCE = 'student_assignment'

// --- Data files API ---

export const DATA_FILES_API_BASE_PATH = '/api/data-files'
export const DATA_FILES_VALIDATION_API_PATH = `${DATA_FILES_API_BASE_PATH}/validate-and-get-upload-options`

// --- File upload limits ---

export const IMAGE_UPLOAD_SIZE_LIMIT_MB = 15
export const IMAGE_UPLOAD_SIZE_LIMIT_BYTES = IMAGE_UPLOAD_SIZE_LIMIT_MB * 1024 * 1024

export const AUDIO_SIZE_LIMIT_MB = 15
export const AUDIO_SIZE_LIMIT_BYTES = AUDIO_SIZE_LIMIT_MB * 1024 * 1024

export const MAX_UPLOAD_FILES = 15
export const UPLOAD_CONCURRENCY_LIMIT = 3

// --- MIME types ---

export const videoMIMETypes =
  'video/x-msvideo, video/mp4, video/mpeg, video/ogg, video/webm' as const
export const imageMIMETypes = 'image/jpeg, image/png, image/webp' as const
export const AudioAttachmentMIMEType =
  'audio/mpeg, audio/wav, audio/aac, audio/ogg, audio/webm' as const

// --- Attachments / embeds ---

export const NOEMBED_BASE_URL = '/api/noembed/open/global/embed'
export const EMBEDDED_VIDEO_URL_MAX_LENGTH = 1000

// --- UI limits ---

export const MAX_VISIBLE_BIO_CHARS = 95

export const COMMUNITY_TITLE_MAX_LENGTH = 255

// --- Timeouts / Sentry ---

export const SENTRY_ENABLED_ENVS = ['staging', 'prod'] as const

const appEnv = process.env.NEXT_PUBLIC_APP_ENV

const isSentryEnv = (value: string): value is (typeof SENTRY_ENABLED_ENVS)[number] =>
  SENTRY_ENABLED_ENVS.includes(value)

export const isSentryEnabled = appEnv != null && isSentryEnv(appEnv)

// --- Date / time ---

const SHORT_TIME_LOCO = {
  s: 'global.time.seconds_short',
  m: 'global.time.minutes_short',
  h: 'global.time.hours_short',
  d: 'global.time.days_short',
  w: 'global.time.weeks_short',
  mo: 'global.time.months_short',
  y: 'global.time.years_short',
} as const satisfies Record<TimeUnits, ParseKeys>

/**
 * Enum representing date units and their corresponding duration in seconds, not milliseconds.
 */
export enum DateUnitEnum {
  second = 1,
  minute = 60,
  hour = 60 * 60,
  day = 60 * 60 * 24,
  week = 60 * 60 * 24 * 7,
  month = 60 * 60 * 24 * 30,
  year = 60 * 60 * 24 * 365,
}

/**
 * A unified constant that maps each time unit to its `Intl.RelativeTimeFormatUnit`
 * and corresponding label. This structure allows for consistent handling of relative
 * time calculations and localized label access.
 *
 * The **order** of items in this array must remain consistent with `DATE_CUTOFFS`.
 * Any changes to this order can result in mismatched units or incorrect calculations.
 *
 * @important
 * The order of this array is tied directly to `DATE_CUTOFFS`. Ensure that both
 * constants are updated together if modifications are required.
 */
export const TIME_UNITS: {
  unit: Intl.RelativeTimeFormatUnit
  shortKey: TimeUnits
  shortLabel: ParseKeys
}[] = [
  {
    unit: 'second',
    shortKey: 's',
    shortLabel: 'comments.components.comments.just_now_label',
  },
  {
    unit: 'minute',
    shortKey: 'm',
    shortLabel: SHORT_TIME_LOCO.m,
  },
  {
    unit: 'hour',
    shortKey: 'h',
    shortLabel: SHORT_TIME_LOCO.h,
  },
  {
    unit: 'day',
    shortKey: 'd',
    shortLabel: SHORT_TIME_LOCO.d,
  },
  {
    unit: 'week',
    shortKey: 'w',
    shortLabel: SHORT_TIME_LOCO.w,
  },
  {
    unit: 'month',
    shortKey: 'mo',
    shortLabel: SHORT_TIME_LOCO.mo,
  },
  {
    unit: 'year',
    shortKey: 'y',
    shortLabel: SHORT_TIME_LOCO.y,
  },
]

/**
 * An array of sequential time thresholds, each representing the upper limit (in seconds)
 * for a corresponding time unit (e.g., minute, hour, day, etc.). This array is directly
 * related to the `TIME_UNITS` constant and is used to determine the most appropriate
 * unit for a given time difference.
 *
 * **Important:** The order of items in this array is critical for determining which unit
 * to use for a given time difference. Changing the order or values will break the mapping
 * logic in related functions.
 *
 * Example usage:
 * ```ts
 * const deltaInSeconds = Math.abs(targetTime - currentTime) / 1000;
 * const unitIndex = DATE_CUTOFFS.findIndex((cutoff) => cutoff > deltaInSeconds);
 * const { unit, shortKey } = TIME_UNITS[unitIndex];
 * console.log(`The appropriate unit is: ${unit} (${shortKey})`);
 * ```
 */
export const DATE_CUTOFFS = [
  DateUnitEnum.minute,
  DateUnitEnum.hour,
  DateUnitEnum.day,
  DateUnitEnum.week,
  DateUnitEnum.month,
  DateUnitEnum.year,
  Infinity,
] as const
